const UserFacingError = require('@/libraries/DigiTickets/Errors/UserFacingError');
const { cloneDeep } = require('@/functions/clone');
const { fixInstanceFieldDataNumbering } = require('@/libraries/DigiTickets/Cart/cartLineCleaning');

/**
 * Note: when we merge a cart with an existing order (as opposed to a stashed cart) we first convert the order back into
 * a cart.
 *
 * @param {Cart} cartA
 * @param {Cart} cartB
 * @param {string} langPrefix
 *
 * @returns {Error|boolean}
 */
const canMergeCarts = (cartA, cartB, langPrefix) => {
    if (!cartA.customer.hasData() || !cartB.customer.hasData()) {
        // Existing or new cart has no customer.
        return true;
    }

    // Both have a customer...
    if (
        cartA.customer.hasAccount()
        && cartB.customer.hasAccount()
        && cartA.customer.account.ID !== cartB.customer.account.ID
    ) {
        // Different accounts.
        return new UserFacingError('Different customer account IDs.', `${langPrefix}.CANNOT_MERGE_ACCOUNT`);
    }

    if (
        !!cartA.customer.getFullName()
        && !!cartB.customer.getFullName()
        && cartA.customer.getFullName().toLowerCase() !== cartB.customer.getFullName().toLowerCase()
    ) {
        // Different names.
        return new UserFacingError('Different customer names.', `${langPrefix}.CANNOT_MERGE_NAME`);
    }

    if (
        !!cartA.customer.getEmail()
        && !!cartB.customer.getEmail()
        && cartA.customer.getEmail().toLowerCase() !== cartB.customer.getEmail().toLowerCase()
    ) {
        // Different emails.
        return new UserFacingError('Different customer emails.', `${langPrefix}.CANNOT_MERGE_EMAIL`);
    }

    return true;
};

/**
 * Combines the items of cart B with the items (and the rest of the contents) of cart A.
 * Does not modify the original carts.
 *
 * @param {Cart} cartA
 * @param {Cart} cartB
 *
 * @returns {Cart}
 */
const mergeCarts = (cartA, cartB) => {
    let canMergeResult = canMergeCarts(cartA, cartB);
    if (canMergeResult instanceof Error) {
        throw canMergeResult;
    }

    /**
     * @type {Cart}
     */
    const combinedCart = cloneDeep(cartA);

    const numLinesInNewCart = combinedCart.itemList.length;

    cartB.itemList.forEach((newLine) => {
        let existingLine = combinedCart.itemList.find((line) => line.getID() === newLine.getID());
        if (existingLine) {
            const existingLineQty = existingLine.getQuantity();
            existingLine.adjustQuantity(newLine.getQuantity());

            // Add the ItemInstances from the newLine to the existingLine.
            for (let i = 0; i < newLine.itemInstances.length; i++) {
                /** @type {ItemInstance} */
                let newInstance = cloneDeep(newLine.itemInstances[i]);
                // Fix the instance number of the new instance. We'll use the same instance number but offset
                // by the qty (number of instances) that were already on the existing line.
                newInstance.setInstance(newInstance.instance + existingLineQty);
                // Add the new instance to the existing line.
                existingLine.itemInstances[existingLineQty + i] = newInstance;
            }

            // Combine membershipDatasetsForRefunds held on the CartLine (these do not have corresponding instances).
            existingLine.membershipDatasetsForRefunds = existingLine.membershipDatasetsForRefunds.concat(
                cloneDeep(newLine.membershipDatasetsForRefunds)
            );
        } else {
            combinedCart.addItem(cloneDeep(newLine));
        }
    });

    // If cart B has any customer data and cart A does not, copy the customer data from B to A.
    if (!cartA.customer.hasData() && cartB.customer.hasData()) {
        combinedCart.customer = cloneDeep(cartB.customer);
    }

    // Reset the lineNumbers and any data that contains the lineNumbers.
    let lineNumber = 1;
    combinedCart.itemList.forEach((line) => {
        line.lineNumber = lineNumber;
        fixInstanceFieldDataNumbering(line);
        ++lineNumber;
    });

    if (combinedCart.originalOrder || numLinesInNewCart === 0) {
        // If we are merging a cart and an existing order rather than a cart an a cart, then we need to copy the stock
        // data from the new cart (cartB) to the combinedOrder (clone of cartA, created from the existing order).
        //
        // We don't need to check each line because any line that is in cartA but not in cartB has already had the stock
        // allocated. If it is in cartA and cartB then the allocation in cartB was based upon the stock levels after the
        // cartA allocation.
        //
        // We don't need to worry about a cart merging with a cart because stock isn't reserved or allocated for a held
        // order so any approvals are invalid anyway and stock levels will be checked when the stashed cart is restored
        // to the cart prior to order completion.
        //
        // If numLinesInNewCart === 0 then we are merging an empty (new) cart with an existing cart so we can add items
        // to the existing cart when we are not on the SellScreen - in this case we also want the stockLevels from
        // the existingCart.
        combinedCart.cartProductStockLevels = cloneDeep(cartB.cartProductStockLevels);
        combinedCart.cartProductQuantities = cloneDeep(cartB.cartProductQuantities);
    }

    return combinedCart;
};

module.exports = {
    canMergeCarts,
    mergeCarts,
};
